<template>
  <div :style="{ minHeight: 'max(884px, 100dvh)' }" class="publish-page text-on-surface pb-32">
    <header class="sticky top-0 z-50 bg-surface shadow-sm flex items-center justify-between px-container-margin h-14">
      <button class="p-2 -ml-2 active:scale-95" type="button" aria-label="关闭" @click="closePage">
        <span class="material-symbols-outlined text-primary">close</span>
      </button>
      <h1 class="font-headline-sm text-headline-sm font-bold text-primary">{{ isEditMode ? '编辑商品' : '发布商品' }}</h1>
      <div class="w-10"></div>
    </header>

    <main class="px-container-margin mt-stack-md pb-8">
      <form id="publishForm" class="publish-form" @submit.prevent="submitPublish">
        <section class="publish-card">
          <div class="image-strip">
            <button
              v-for="(slot, idx) in imageSlots"
              :key="idx"
              class="image-slot"
              :class="{ 'has-image': slot.preview }"
              type="button"
              @click="openImagePicker(idx)"
            >
              <img v-if="slot.preview" :src="slot.preview" alt="" />
              <template v-else>
                <span class="material-symbols-outlined">{{ idx === 0 ? 'add_a_photo' : 'add' }}</span>
                <span v-if="idx === 0">添加主图</span>
              </template>
              <span v-if="slot.status === 'uploading'" class="slot-mask">上传中</span>
              <span v-if="slot.status === 'error'" class="slot-mask is-error">重选</span>
              <span v-if="slot.status === 'uploaded'" class="slot-check material-symbols-outlined">check</span>
            </button>
          </div>
          <p class="font-label-md text-label-md text-on-surface-variant">{{ imageUploadHint }}</p>
          <input ref="imageInput" accept="image/*" class="hidden" type="file" multiple @change="onImagesPicked" />

          <textarea
            v-model="form.description"
            class="post-textarea"
            placeholder="描述一下宝贝的品牌型号、亮点来源、适合谁带走...&#10;第一行会自动作为标题"
            rows="6"
            required
          ></textarea>
        </section>

        <section class="publish-card publish-fields">
          <div class="field-row">
            <span>价格</span>
            <label class="price-input">
              <span>£</span>
              <input v-model="form.price" type="number" min="0" step="0.01" placeholder="0.00" required />
            </label>
          </div>

          <button class="field-row" type="button" @click="showCategoryPicker = true">
            <span>分类</span>
            <strong>{{ form.category || (categoriesLoading ? '加载中...' : '请选择') }}</strong>
          </button>

          <div class="field-block">
            <div class="field-block-head">
              <span>选择圈子</span>
              <small>单选</small>
            </div>
            <div v-if="circleOptions.length" class="chip-row">
              <button
                v-for="circle in circleOptions"
                :key="circle.id"
                class="select-chip"
                :class="{ 'is-active': selectedCircleId === Number(circle.id) }"
                type="button"
                @click="selectCircle(circle)"
              >{{ circle.title || circle.momo || circle.city }}</button>
            </div>
            <p v-else class="empty-hint">还没有绑定圈子，请先到资料页设置。</p>
          </div>

          <div class="field-block">
            <div class="field-block-head">
              <span>所在位置</span>
              <button type="button" @click="openPositionPicker">常用位置</button>
            </div>
            <input
              v-model="form.place"
              class="plain-input"
              placeholder="默认使用所选圈子的城市或国家，可修改"
              required
              type="text"
            />
          </div>

          <div class="field-block">
            <div class="field-block-head">
              <span>手机号码<span class="required-mark">*</span></span>
            </div>
            <div class="publish-phone-row">
              <select v-model="dial" class="publish-phone-select" aria-label="区号">
                <option value="+44">+44</option>
                <option value="+86">+86</option>
                <option value="+1">+1</option>
                <option value="+81">+81</option>
                <option value="+65">+65</option>
                <option value="+852">+852</option>
              </select>
              <span aria-hidden="true" class="publish-phone-divider"></span>
              <input
                v-model="mobile"
                class="publish-phone-input"
                inputmode="tel"
                :placeholder="dial === '+44' ? '10位手机号，勿加0' : '请输入手机号'"
                required
                type="tel"
              />
            </div>
            <p class="field-hint">填写后，买家／卖家可直接通过 WhatsApp 与你联系。</p>
          </div>
        </section>

        <p v-if="categoryError" class="error-line">{{ categoryError }}</p>
        <p v-if="circleError" class="error-line">{{ circleError }}</p>
      </form>
    </main>

    <div class="publish-footer">
      <button
        class="publish-submit"
        type="submit"
        form="publishForm"
        :disabled="submitting"
      >
        <span v-if="submitting" class="material-symbols-outlined animate-spin">progress_activity</span>
        {{ submitting ? (isEditMode ? '保存中...' : '发布中...') : (isEditMode ? '保存修改' : '立即发布') }}
      </button>
    </div>

    <div v-if="showCategoryPicker" class="sheet-mask" @click.self="showCategoryPicker = false">
      <div class="bottom-sheet">
        <h3>选择分类</h3>
        <div v-if="categoriesLoading" class="sheet-empty">分类加载中...</div>
        <div v-else class="sheet-grid">
          <button
            v-for="cat in categoryOptions"
            :key="cat.id"
            class="sheet-option"
            :class="{ 'is-active': selectedCategoryId === cat.id }"
            type="button"
            @click="selectCategory(cat)"
          >{{ cat.title }}</button>
        </div>
      </div>
    </div>

    <div v-if="showPositionPicker" class="sheet-mask" @click.self="showPositionPicker = false">
      <div class="bottom-sheet">
        <h3>常用位置</h3>
        <div v-if="commonPositions.length" class="position-list">
          <button
            v-for="place in commonPositions"
            :key="place"
            class="position-option"
            type="button"
            @click="chooseCommonPosition(place)"
          >{{ place }}</button>
        </div>
        <p v-else class="sheet-empty">暂无常用位置，发布后会自动记录。</p>
      </div>
    </div>
  </div>
</template>

<script>
import getApi from '@/api';
import store from '@/store';

const api = getApi();
const API_IMAGE_BASE = 'https://api.pandacampus.com';
const COMMON_PLACES_KEY = 'publishCommonPlaces';
const fallbackCategories = [
  { id: 0, title: '其他' }
];

export default {
  name: 'PublishPage',
  data() {
    return {
      categoryOptions: [],
      categoriesLoading: false,
      categoryError: '',
      circleError: '',
      showCategoryPicker: false,
      showPositionPicker: false,
      submitting: false,
      editingGoodsId: '',
      editLoading: false,
      form: {
        price: '',
        category: '',
        description: '',
        place: ''
      },
      imageSlots: this.createEmptyImageSlots(),
      currentImageSlot: 0,
      selectedCategoryId: 0,
      circleOptions: [],
      selectedCircleId: 0,
      commonPositions: [],
      dial: '+44',
      mobile: ''
    };
  },
  computed: {
    isEditMode() {
      return !!this.editingGoodsId;
    },
    uploadingImageCount() {
      return this.imageSlots.filter(slot => slot.status === 'uploading').length;
    },
    failedImageCount() {
      return this.imageSlots.filter(slot => slot.status === 'error').length;
    },
    imageUploadHint() {
      if (this.uploadingImageCount) return this.uploadingImageCount + ' 张图片上传中，请稍候。';
      if (this.failedImageCount) return this.failedImageCount + ' 张图片上传失败，请重新选择。';
      return '第一张为主图，建议上传清晰实拍图。';
    }
  },
  methods: {
    ensureLoggedIn() {
      if (store.state.token) return true;
      sessionStorage.setItem('pendingToast', '未登录，请先登录');
      this.$router.replace({ name: 'home' });
      return false;
    },
    normalizePhone(value, dial) {
      let mobile = String(value || '').replace(/\D/g, '');
      const code = dial || '+44';
      if ((code === '+44' || code === '44') && mobile.length === 11 && mobile[0] === '0') {
        mobile = mobile.slice(1);
      }
      return mobile;
    },
    isValidPhone(mobile, dial) {
      if (!mobile) return false;
      if (dial === '+44' || dial === '44') return /^\d{10}$/.test(mobile);
      if (dial === '+86' || dial === '86') return /^1\d{10}$/.test(mobile);
      return /^\d{7,15}$/.test(mobile);
    },
    fillContactFromProfile() {
      const profile = store.state.userinfo || {};
      this.dial = profile.code || '+44';
      if (this.dial && this.dial.charAt(0) !== '+') this.dial = '+' + String(this.dial).replace(/\D/g, '');
      this.mobile = profile.mobile || '';
    },
    async syncContactToProfile() {
      const dial = this.dial || '+44';
      const mobile = this.normalizePhone(this.mobile, dial);
      const profile = store.state.userinfo || {};
      if (profile.mobile === mobile && (profile.code || '+44') === dial) return;
      const payload = {
        username: profile.username || '',
        bio: profile.bio || '',
        gender: profile.gender != null ? String(profile.gender) : '0',
        mobile,
        code: dial,
        email: profile.email || ''
      };
      const avatar = String(profile.avatar || '').trim();
      if (avatar && !/^data:image\//i.test(avatar) && !/^blob:/i.test(avatar) && avatar.length <= 255) {
        payload.avatar = avatar;
      }
      const tags = profile.tags || (profile.circle_list || []).map(function (c) { return c.id; }).filter(Boolean).join(',');
      if (tags) payload.tags = tags;
      try {
        await api.profile(payload, store.state.token);
        store.setUserinfo(Object.assign({}, profile, { mobile, code: dial }));
      } catch (_) {
        // 发布仍可继续；WhatsApp 依赖资料同步，失败时提示
      }
    },
    closePage() {
      if (this.isEditMode) {
        this.clearEditingMode();
        this.$router.push({ name: 'my-products' });
        return;
      }
      this.$router.push({ name: 'home' });
    },

    createEmptyImageSlots() {
      return [
        { preview: null, file: null, uploadedUrl: '', status: 'empty', error: '' },
        { preview: null, file: null, uploadedUrl: '', status: 'empty', error: '' },
        { preview: null, file: null, uploadedUrl: '', status: 'empty', error: '' },
        { preview: null, file: null, uploadedUrl: '', status: 'empty', error: '' }
      ];
    },

    normalizeImageUrl(src) {
      if (!src) return '';
      if (/^https?:\/\//i.test(src)) return src;
      if (src.charAt(0) === '/') return API_IMAGE_BASE + src;
      return src;
    },

    deriveTitle(text) {
      const firstLine = String(text || '').split(/\r?\n/).map(line => line.trim()).find(Boolean);
      return firstLine || '未命名商品';
    },

    circleAddress(circle) {
      if (!circle) return '';
      const city = circle.city || '';
      const country = circle.contry || circle.country || '';
      return [city, country].filter(Boolean).join(', ') || circle.title || circle.momo || '';
    },

    loadCommonPositions() {
      try {
        const parsed = JSON.parse(localStorage.getItem(COMMON_PLACES_KEY) || '[]');
        this.commonPositions = Array.isArray(parsed) ? parsed.filter(Boolean).slice(0, 8) : [];
      } catch (_) {
        this.commonPositions = [];
      }
    },

    rememberCommonPosition(place) {
      const value = String(place || '').trim();
      if (!value) return;
      const next = [value].concat(this.commonPositions.filter(item => item !== value)).slice(0, 8);
      this.commonPositions = next;
      localStorage.setItem(COMMON_PLACES_KEY, JSON.stringify(next));
    },

    openPositionPicker() {
      this.loadCommonPositions();
      this.showPositionPicker = true;
    },

    chooseCommonPosition(place) {
      this.form.place = place;
      this.showPositionPicker = false;
    },

    fetchCategories() {
      this.categoriesLoading = true;
      this.categoryError = '';
      api.categories(store.state.token || '')
        .then(list => {
          const mapped = (list || []).map(cat => ({
            id: Number(cat.id) || 0,
            title: cat.title || ''
          })).filter(cat => cat.id && cat.title);
          this.categoryOptions = mapped.length ? mapped : fallbackCategories;
          if (this.selectedCategoryId) {
            this.syncSelectedCategoryLabel();
          } else if (this.categoryOptions.length) {
            this.selectCategory(this.categoryOptions[0], { close: false });
          }
        })
        .catch(err => {
          this.categoryOptions = fallbackCategories;
          this.categoryError = err.message || '分类加载失败';
        })
        .finally(() => {
          this.categoriesLoading = false;
        });
    },

    fetchCircles() {
      this.circleError = '';
      if (!store.state.token) {
        this.circleOptions = this.normalizeCircles(store.state.userinfo && store.state.userinfo.circle_list);
        this.selectDefaultCircle();
        return Promise.resolve(this.circleOptions);
      }
      return api.userInfo(store.state.token || '')
        .then(profile => {
          store.setUserinfo(profile || {});
          this.circleOptions = this.normalizeCircles(profile && profile.circle_list);
          this.selectDefaultCircle();
          return this.circleOptions;
        })
        .catch(error => {
          this.circleOptions = this.normalizeCircles(store.state.userinfo && store.state.userinfo.circle_list);
          this.selectDefaultCircle();
          this.circleError = error.message || '';
          return this.circleOptions;
        });
    },

    normalizeCircles(list) {
      if (!Array.isArray(list)) return [];
      return list.filter(circle => circle && circle.id != null).map(circle => ({
        id: Number(circle.id) || 0,
        title: circle.title || '',
        momo: circle.momo || '',
        city: circle.city || '',
        contry: circle.contry || circle.country || ''
      }));
    },

    selectDefaultCircle() {
      if (this.selectedCircleId || !this.circleOptions.length) return;
      this.selectCircle(this.circleOptions[0], { preservePlace: !!this.form.place });
    },

    selectCircle(circle, options) {
      options = options || {};
      this.selectedCircleId = Number(circle.id) || 0;
      const place = this.circleAddress(circle);
      if (!options.preservePlace || !this.form.place) {
        this.form.place = place;
      }
    },

    selectCategory(cat, options) {
      options = options || {};
      this.selectedCategoryId = Number(cat.id) || 0;
      this.form.category = cat.title || '';
      if (options.close !== false) {
        this.showCategoryPicker = false;
      }
    },

    syncSelectedCategoryLabel() {
      if (!this.selectedCategoryId || !this.categoryOptions.length) return;
      const selected = this.categoryOptions.find(cat => Number(cat.id) === Number(this.selectedCategoryId));
      if (selected) this.form.category = selected.title;
    },

    refreshEditMode() {
      const editingGoodsId = sessionStorage.getItem('editingGoodsId') || '';
      if (editingGoodsId && editingGoodsId !== this.editingGoodsId) {
        this.editingGoodsId = editingGoodsId;
        this.loadEditingGoods();
      } else if (!editingGoodsId && this.editingGoodsId) {
        this.clearEditingMode({ keepForm: false });
      }
    },

    clearEditingMode(options) {
      options = options || {};
      sessionStorage.removeItem('editingGoodsId');
      this.editingGoodsId = '';
      this.editLoading = false;
      if (options.keepForm) return;
      this.form = {
        price: '',
        category: '',
        description: '',
        place: ''
      };
      this.imageSlots = this.createEmptyImageSlots();
      this.currentImageSlot = 0;
      this.selectedCategoryId = 0;
      this.selectedCircleId = 0;
    },

    loadEditingGoods() {
      if (!this.editingGoodsId) return;
      this.editLoading = true;
      api.goodsInfo(this.editingGoodsId, store.state.token || '')
        .then(data => {
          this.applyEditingGoods(data);
        })
        .catch(error => {
          alert(error.message || '商品详情加载失败');
        })
        .finally(() => {
          this.editLoading = false;
        });
    },

    applyEditingGoods(data) {
      data = data || {};
      const images = String(data.images || data.image || '')
        .split(',')
        .map(url => this.normalizeImageUrl(url.trim()))
        .filter(Boolean);
      const selectedTypeId = Number(data.type_id || data.typeId || data.category_id || 0) || 0;

      this.form.price = data.price == null ? '' : String(data.price);
      this.form.description = data.introduction || data.introudction || data.description || data.title || '';
      this.form.category = data.type_title || data.category || '';
      this.form.place = data.place || this.form.place;
      this.selectedCategoryId = selectedTypeId;
      this.selectedCircleId = Number(data.circle_id || 0) || this.selectedCircleId;
      this.syncSelectedCategoryLabel();
      this.setImageSlotsFromUrls(images);
    },

    setImageSlotsFromUrls(urls) {
      const slots = this.createEmptyImageSlots();
      urls.slice(0, slots.length).forEach((url, index) => {
        slots[index] = {
          preview: url,
          file: null,
          uploadedUrl: url,
          status: 'uploaded',
          error: ''
        };
      });
      this.imageSlots = slots;
      this.currentImageSlot = Math.min(urls.length, slots.length);
    },

    openImagePicker(idx) {
      this.currentImageSlot = idx;
      this.$refs.imageInput.click();
    },

    async onImagesPicked(event) {
      const files = Array.from(event.target.files);
      const uploadTasks = [];
      files.forEach(file => {
        const url = URL.createObjectURL(file);
        if (this.currentImageSlot < this.imageSlots.length) {
          const slot = this.imageSlots[this.currentImageSlot];
          slot.preview = url;
          slot.file = file;
          slot.uploadedUrl = '';
          slot.status = 'uploading';
          slot.error = '';
          uploadTasks.push(
            api.uploadFile(file)
              .then(data => {
                const uploadedUrl = this.normalizeImageUrl(data.fullurl || data.url || '');
                if (!uploadedUrl) {
                  throw new Error('图片上传失败');
                }
                slot.uploadedUrl = uploadedUrl;
                slot.status = 'uploaded';
              })
              .catch(err => {
                slot.uploadedUrl = '';
                slot.status = 'error';
                slot.error = err.message || '图片上传失败';
              })
          );
          this.currentImageSlot++;
        }
      });
      event.target.value = '';
      await Promise.all(uploadTasks);
    },

    getUploadedUrls() {
      return this.imageSlots
        .map(slot => slot.uploadedUrl)
        .filter(Boolean);
    },

    validatePublish(uploadedUrls) {
      if (!this.form.description.trim()) return '请填写帖子内容';
      if (!this.form.price) return '请输入价格';
      if (!this.selectedCategoryId) return '请选择分类';
      if (!this.selectedCircleId) return '请选择圈子';
      if (!this.form.place.trim()) return '请填写所在位置';
      const mobile = this.normalizePhone(this.mobile, this.dial);
      if (!this.isValidPhone(mobile, this.dial)) {
        return this.dial === '+44'
          ? '英国手机号请填写 10 位数字，号码前不用加 0'
          : '请填写正确的手机号码';
      }
      if (!uploadedUrls.length) return '请至少上传一张商品主图';
      if (this.uploadingImageCount) return '图片正在上传，请稍后再发布';
      if (this.failedImageCount) return '有图片上传失败，请重新选择图片后再发布';
      return '';
    },

    buildPublishPayload(uploadedUrls) {
      return {
        image: uploadedUrls[0],
        images: uploadedUrls.join(','),
        title: this.deriveTitle(this.form.description),
        price: parseFloat(this.form.price),
        introudction: this.form.description.trim(),
        place: this.form.place.trim(),
        circle_id: this.selectedCircleId,
        type_id: this.selectedCategoryId
      };
    },

    async submitPublish() {
      if (this.submitting) return;
      const uploadedUrls = this.getUploadedUrls();
      const validationError = this.validatePublish(uploadedUrls);
      if (validationError) {
        alert(validationError);
        return;
      }

      this.submitting = true;

      try {
        const payload = this.buildPublishPayload(uploadedUrls);

        if (this.isEditMode) {
          await api.modifyGoods(this.editingGoodsId, payload, store.state.token || '');
          await this.syncContactToProfile();
          this.rememberCommonPosition(payload.place);
          if (window.navigator.vibrate) window.navigator.vibrate(200);
          this.clearEditingMode();
          this.$router.push({ name: 'my-products' });
        } else {
          const created = await api.createGoods(payload, store.state.token || '');
          await this.syncContactToProfile();
          this.rememberCommonPosition(payload.place);
          if (window.navigator.vibrate) window.navigator.vibrate(200);
          const goodsId = (created && (created.id || created.goods_id)) || '';
          if (goodsId) {
            store.setActiveGoodsId(String(goodsId));
            sessionStorage.setItem('activeGoodsId', String(goodsId));
          }
          sessionStorage.setItem('publishSuccessPreview', JSON.stringify({
            id: goodsId,
            img: payload.image,
            title: payload.title,
            location: payload.place,
            price: '£' + Number(payload.price).toFixed(2)
          }));
          this.clearEditingMode({ keepForm: false });
          this.fillContactFromProfile();
          sessionStorage.setItem('publishFormReset', '1');
          this.$router.push({ name: 'publish-success' });
        }
      } catch (error) {
        alert(error.message || '发布失败，请稍后再试');
      } finally {
        this.submitting = false;
      }
    }
  },
  mounted() {
    if (!this.ensureLoggedIn()) return;
    this.fillContactFromProfile();
    this.loadCommonPositions();
    this.fetchCategories();
    this.fetchCircles();
    this.refreshEditMode();
  },
  activated() {
    if (!this.ensureLoggedIn()) return;
    this.fillContactFromProfile();
    this.fetchCircles();
    const editingGoodsId = sessionStorage.getItem('editingGoodsId') || '';
    const needReset = sessionStorage.getItem('publishFormReset') === '1';
    if (needReset) {
      sessionStorage.removeItem('publishFormReset');
      this.clearEditingMode({ keepForm: false });
      this.fillContactFromProfile();
      return;
    }
    this.refreshEditMode();
    if (!editingGoodsId && this.editingGoodsId) {
      this.clearEditingMode({ keepForm: false });
      this.fillContactFromProfile();
    }
  }
};
</script>

<style scoped>
.publish-page {
  background: #f7f7f8;
  font-family: 'Inter', sans-serif;
  -webkit-tap-highlight-color: transparent;
}
.publish-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.publish-card {
  background: #fff;
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}
.image-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}
.image-slot {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  border: 1px dashed #d7d4dd;
  background: #f4f1f8;
  color: #6750a4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  overflow: hidden;
  font-size: 12px;
}
.image-slot.has-image {
  border-style: solid;
}
.image-slot img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.slot-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.46);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}
.slot-mask.is-error {
  background: rgba(186, 26, 26, 0.78);
}
.slot-check {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #386a20;
  color: #fff;
  font-size: 14px;
}
.post-textarea,
.plain-input {
  width: 100%;
  border: none;
  border-radius: 8px;
  background: #f4f1f8;
  padding: 12px;
  font-size: 15px;
  resize: none;
}
.publish-phone-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 8px;
  background: #f4f1f8;
}
.publish-phone-select {
  width: 72px;
  flex-shrink: 0;
  border: none;
  background-color: transparent;
  outline: none;
  font-size: 15px;
  font-weight: 600;
  color: #1a1c1d;
  padding: 0 16px 0 0;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='%23837560'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 2px center;
}
.publish-phone-divider {
  width: 1px;
  height: 18px;
  flex-shrink: 0;
  background: rgba(131, 117, 96, 0.25);
}
.publish-phone-input {
  flex: 1;
  min-width: 0;
  width: auto;
  border: none;
  background: transparent;
  outline: none;
  font-size: 15px;
  color: #1a1c1d;
  padding: 12px 0;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #837560;
}
.required-mark {
  color: #ba1a1a;
  margin-left: 2px;
}
.post-textarea {
  margin-top: 10px;
}
.publish-fields {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.field-row {
  min-height: 48px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #eeeeef;
  text-align: left;
}
.field-row strong {
  color: #6750a4;
  font-size: 14px;
}
.price-input {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  color: #ba1a1a;
  font-weight: 700;
}
.price-input input {
  width: 96px;
  border: none;
  background: transparent;
  text-align: right;
  color: #ba1a1a;
  font-weight: 700;
}
.field-block {
  padding: 12px 0 4px;
  border-bottom: 1px solid #eeeeef;
}
.field-block:last-child {
  border-bottom: none;
}
.field-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
.field-block-head button,
.field-block-head small {
  color: #6750a4;
  font-size: 12px;
}
.chip-row {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
}
.select-chip {
  height: 32px;
  padding: 0 12px;
  border-radius: 9999px;
  border: 1px solid #ded8e8;
  background: #fff;
  color: #49454f;
  white-space: nowrap;
  font-size: 13px;
}
.select-chip.is-active {
  border-color: transparent;
  background: #eaddff;
  color: #21005d;
}
.empty-hint,
.error-line {
  color: #ba1a1a;
  font-size: 13px;
}
.publish-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 55;
  background: rgba(247, 247, 248, 0.96);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid #eeeeef;
  padding: 12px 16px max(16px, env(safe-area-inset-bottom, 16px));
}
.publish-submit {
  width: 100%;
  min-height: 50px;
  border-radius: 9999px;
  background: #ffd54f;
  color: #2b2300;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.sheet-mask {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(0, 0, 0, 0.38);
  display: flex;
  align-items: flex-end;
}
.bottom-sheet {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  background: #fff;
  border-radius: 18px 18px 0 0;
  padding: 18px 16px max(18px, env(safe-area-inset-bottom, 18px));
}
.bottom-sheet h3 {
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 12px;
}
.sheet-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}
.sheet-option,
.position-option {
  min-height: 40px;
  border-radius: 8px;
  background: #f4f1f8;
  color: #49454f;
  font-size: 14px;
}
.sheet-option.is-active {
  background: #eaddff;
  color: #21005d;
  font-weight: 700;
}
.position-list {
  display: grid;
  gap: 8px;
}
.position-option {
  text-align: left;
  padding: 0 12px;
}
.sheet-empty {
  color: #79747e;
  font-size: 14px;
  padding: 14px 0;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
